import React from 'react';

import { decimal } from 'Helpers/currencyFormat';
import { formatFigure } from 'Helpers/formatService';
import { MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';

import LocalisedNumberFormat from 'CommonComponents/LocalisedNumberFormat';

export const EM_DASH = '—';

export const GROUPING_ROLE = 'role';
export const GROUPING_PERSON = 'person';
export const GROUPING_MONTH = 'month';

export const TYPE_ALL = 'all';
export const TYPE_INTERNAL = 'internal';
export const TYPE_FREELANCE = 'freelance';

export const PERIOD_ALL_TIME = 'all_time';
export const PERIOD_THIS_MONTH = 'this_month';
export const PERIOD_LAST_MONTH = 'last_month';
export const PERIOD_THIS_QUARTER = 'this_quarter';
export const PERIOD_THIS_YEAR = 'this_year';
export const PERIOD_CUSTOM = 'custom';

/**
 * Money, to the nearest whole unit. The report reads in day rates and totals of
 * them, so cents add width without adding information — and the values keep
 * their full precision in the payload, so nothing reconciles differently.
 */
export function TimeReportMoney({ value, currency }) {
    return <LocalisedNumberFormat value={value ?? 0} displayType="text" thousandSeparator={true} decimalScale={0} prefix={currency?.symbol ?? ''} />;
}

/**
 * Days always carry two decimals — the precision they are stored at, and the
 * precision a quarter day needs. Rounded to one, 0.25 reads 0.3 and a column of
 * quarter days stops adding up to its own total.
 */
export function TimeReportDays({ value }) {
    return <LocalisedNumberFormat value={value ?? 0} displayType="text" thousandSeparator={true} decimalScale={2} fixedDecimalScale={true} />;
}

/** A percentage, or an em dash when there was nothing to divide by. */
export function formatPercent(value, precision = 0) {
    if (value === null || value === undefined) {
        return EM_DASH;
    }

    return `${Number(value).toFixed(precision)}%`;
}

// Stands in for the figure itself, so the shared rule decides blank / dash / "<1"
// and only a real figure reaches the number component. The floor's own step is
// formatted in the locale, like the number component.
const RENDER_FIGURE = '\u0000';

const tableFigure = (value, precision, render) => {
    const text = formatFigure(value, (v, p) => (v === value ? RENDER_FIGURE : decimal(v, p)), { precision });

    return text === RENDER_FIGURE ? render() : text;
};

/** TimeReportMoney in a table cell: blank when nothing was sent, the dash for a zero. */
export function TimeReportMoneyCell({ value, currency }) {
    return tableFigure(value, 0, () => <TimeReportMoney value={value} currency={currency} />);
}

/** TimeReportDays in a table cell: blank when nothing was sent, the dash for a zero. */
export function TimeReportDaysCell({ value }) {
    return tableFigure(value, 2, () => <TimeReportDays value={value} />);
}

/**
 * formatPercent in a table cell: the dash when there was nothing to divide by or
 * the figure is zero.
 */
export function formatPercentCell(value, precision = 0) {
    if (value === null || value === undefined) {
        return MARGIN_NO_DIVISOR;
    }

    return formatFigure(value, (figure, places) => Number(figure).toFixed(places), { precision, suffix: '%' });
}
